import React, { useState, useEffect } from 'react';

import { useSnackbar } from 'notistack';

import Paper from '@material-ui/core/Paper';
import { LinearProgress } from '@material-ui/core';
import RefreshIcon from '@mui/icons-material/Refresh';

import { ISourceSetEntity } from '../../../state/types';
import { getUsers } from '../../../state/app/collections';

import TranslationHelper from '../../../helpers/TranslationHelper';
import { useAppDispatch, useToolkitDispatch } from '../../../hooks';

import PaneHeader from '../../../components/PaneHeader';

import { useStyles } from './Themable.hooks';
import { fetchTickets, fetchWorkflows, selectTicketId } from '../state';
import AppConfig from '../../../constants/AppConfig';
import ServerSideRowModelGrid from '../../../components/ServerSideRowModelGrid';
import InfiniteRowModelGrid from '../../../components/InfiniteRowModelGrid';
import {
    ColDef,
    GridApi,
    GridReadyEvent,
    IDatasource,
    IGetRowsParams,
    IServerSideDatasource,
    IServerSideGetRowsParams,
} from 'ag-grid-community';
import {
    generateDomainDataColumns,
    getColDefs,
    gridProps,
    serverSideGridProps,
} from './utils/gridConfig';
import {
    useFilters,
    useSelectedWorkflow,
    useTicketsStatus,
    useWorkflows,
} from '../state/index.hooks';
import { Box } from '@mui/material';
import { useIserId, useUserSettings } from '../../../state/user/index.hooks';
import { useUsers } from '../../../state/app/collections/index.hooks';
import TicketGridFilters from './components';
import IconButtonWithTooltip from '../../../components/IconButtonWithTooltip';
import { errorMessageHandler } from '../../../helpers/errorMessageHandler';

const TICKETS_LIMIT = 25;

interface IOwnProps {
    gridApi: GridApi | null;
    setGridApi: (gridApi: GridApi | null) => void;
    refreshGrid: () => void;
}

const TicketsGrid = ({ gridApi, setGridApi, refreshGrid }: IOwnProps) => {
    const classes = useStyles();
    const [dataSourceLoading, setDataSourceLoading] = useState<boolean>(true);

    const [domainColumns, setDomainColumns] = useState<ColDef[] | null>(null);
    const status = useTicketsStatus();
    const selectedWorkflow = useSelectedWorkflow();
    const userId = useIserId();
    const users = useUsers();
    const filters = useFilters();
    const { enqueueSnackbar } = useSnackbar();

    const assignedToMe = filters.assignedToMe;
    const selectedStatuses = filters.statuses;
    const enterpriseKey = AppConfig?.instance.getConfigKey(
        AppConfig.PROPERTY_GRID
    )?.key;
    const workflows = useWorkflows();
    const userSettings = useUserSettings();

    const toolkitDispatch = useToolkitDispatch();
    const dispatch = useAppDispatch();

    const availableStatuses = workflows?.find(
        (wf) => wf.name === selectedWorkflow
    )?.statuses;

    useEffect(() => {
        toolkitDispatch(fetchWorkflows());
        dispatch(getUsers());
    }, []);

    useEffect(() => {
        if (gridApi) {
            gridApi.setServerSideDatasource(ticketsDataSourceEnterprise);
        }
    }, [selectedWorkflow, assignedToMe, selectedStatuses]);

    const handleCellInteraction = (data: ISourceSetEntity) => {
        toolkitDispatch(selectTicketId(data.id));
    };

    const getRowsData = (params: IGetRowsParams) => ({
        start: params.startRow,
        end: params.endRow,
    });

    const getRowsDataServerSide = (params: IServerSideGetRowsParams) => ({
        start: params.request.startRow,
        end: params.request.endRow,
    });

    const getRows = (params: IGetRowsParams | IServerSideGetRowsParams) => {
        if (!selectedWorkflow) {
            setDataSourceLoading(false);

            params.successCallback([], 0);
            gridApi?.showNoRowsOverlay();
            return;
        }

        setDataSourceLoading(true);
        gridApi?.hideOverlay();

        const { start } = enterpriseKey
            ? getRowsDataServerSide(params as IServerSideGetRowsParams)
            : getRowsData(params as IGetRowsParams);

        const page = Math.floor((start || 0) / TICKETS_LIMIT) || 0;

        toolkitDispatch(
            fetchTickets({
                assignedUser: assignedToMe ? userId : undefined,
                workflow: selectedWorkflow,
                page,
                size: TICKETS_LIMIT,
                statuses:
                    selectedStatuses.length > 0
                        ? selectedStatuses.map((s) => s.id).join(',')
                        : undefined,
            })
        )
            .unwrap()
            .then((data) => {
                const { content, totalSize } = data;
                params.successCallback(content, totalSize);

                if (totalSize === 0) {
                    gridApi?.showNoRowsOverlay();
                }

                if (page === 0) {
                    const domainCols = generateDomainDataColumns(
                        content,
                        userSettings
                    );
                    setDomainColumns(domainCols);
                }
            })
            .catch((e) => {
                params.successCallback([], 0);
                const message = errorMessageHandler(e.status)();
                enqueueSnackbar(message, {
                    variant: 'error',
                });
            })
            .finally(() => {
                setDataSourceLoading(false);
            });
    };

    const ticketsDataSource: IDatasource = { getRows };

    const ticketsDataSourceEnterprise: IServerSideDatasource = { getRows };

    const setDataSource = (api: GridApi) => {
        if (enterpriseKey) {
            api.setServerSideDatasource(ticketsDataSourceEnterprise);
        } else {
            api.setDatasource(ticketsDataSource);
        }
    };
    const onGridReady = ({ api }: GridReadyEvent) => {
        setGridApi(api);
        setDataSource(api);
    };

    useEffect(() => {
        if (gridApi && domainColumns && workflows && availableStatuses) {
            gridApi?.setColumnDefs([
                ...getColDefs(
                    users,
                    workflows,
                    availableStatuses,
                    userSettings
                ),
                ...domainColumns,
            ]);
        }
    }, [domainColumns, gridApi, workflows, availableStatuses]);

    return (
        <Paper className={classes.pane}>
            <div className={classes.wrapper}>
                <PaneHeader
                    title={TranslationHelper.translate('Tickets')}
                    renderRightCustomControls={() => (
                        <IconButtonWithTooltip
                            aria-label={TranslationHelper.translate('Refresh')}
                            title={TranslationHelper.translate('Refresh')}
                            onClick={refreshGrid}
                        >
                            <RefreshIcon />
                        </IconButtonWithTooltip>
                    )}
                    renderLeftCustomControls={() => (
                        <TicketGridFilters
                            availableStatuses={availableStatuses}
                        />
                    )}
                />
                {status === 'idle' && (
                    <Box
                        sx={{
                            p: 1,
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {TranslationHelper.translate(
                            'Select category to load tickets'
                        )}
                    </Box>
                )}
                {(dataSourceLoading || status === 'loading') && (
                    <LinearProgress />
                )}
                {enterpriseKey ? (
                    <ServerSideRowModelGrid
                        onGridReady={onGridReady}
                        onRowClicked={(event) =>
                            handleCellInteraction(event.data)
                        }
                        {...serverSideGridProps}
                    />
                ) : (
                    <InfiniteRowModelGrid
                        onGridReady={onGridReady}
                        onRowClicked={(event) =>
                            handleCellInteraction(event.data)
                        }
                        {...gridProps}
                    />
                )}
            </div>
        </Paper>
    );
};

export default TicketsGrid;
